import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  MapIcon,
  MapPinIcon,
  ViewColumnsIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import SearchBar from '../Search/SearchBar';
import { Modal } from '../common/UIComponents';
import {
  DEFAULT_DISCOVERY_SORT,
  DISCOVERY_HOURLY_RATE_PRESETS,
  formatDiscoveryIssueTypeLabel,
  formatHourlyRateFilterLabel,
  formatServiceDiscoveryCountLabel,
  type ServiceDiscoveryIssueType,
  type ServiceDiscoverySort,
} from '../../utils/serviceDiscovery';
import type { ServiceLocationInfo } from '../../types/firestore';
import type { SmartSearchSelection } from '../../utils/smartSearch';

type DiscoveryCityFieldProps = {
  value: string;
  cities: string[];
  disabled: boolean;
  onChange: (city: string) => void;
};

type DiscoverySortFieldProps = {
  sortBy: ServiceDiscoverySort;
  disabled: boolean;
  onChange: (sort: ServiceDiscoverySort) => void;
  className?: string;
};

type DiscoveryFilterPanelProps = {
  activeView: 'map' | 'list';
  disabled: boolean;
  sortBy: ServiceDiscoverySort;
  issueType: ServiceDiscoveryIssueType;
  hourlyRateMax: number | null;
  includeUnpriced: boolean;
  onSortChange: (sort: ServiceDiscoverySort) => void;
  onIssueTypeChange: (issueType: ServiceDiscoveryIssueType) => void;
  onHourlyRateMaxChange: (value: number | null) => void;
  onIncludeUnpricedChange: (value: boolean) => void;
};

type DiscoveryFilterSheetProps = DiscoveryFilterPanelProps & {
  open: boolean;
  resultsCount?: number;
  resultsCountLoading: boolean;
  showClearAllAction: boolean;
  onClose: () => void;
  onResetFilters: () => void;
  returnFocusRef: React.RefObject<HTMLElement>;
};

interface ServiceDiscoveryControlsProps {
  activeView: 'map' | 'list';
  mapUrl: string;
  listUrl: string;
  resultsCount?: number;
  resultsCountLoading?: boolean;
  showResultsCount?: boolean;
  query: string;
  city: string;
  sortBy: ServiceDiscoverySort;
  issueType: ServiceDiscoveryIssueType;
  hourlyRateMax: number | null;
  includeUnpriced: boolean;
  cities: string[];
  services: ServiceLocationInfo[];
  smartSelection: SmartSearchSelection | null;
  disabled?: boolean;
  onSearchChange: (item: SmartSearchSelection | null) => void;
  onCityChange: (city: string) => void;
  onSortChange: (sort: ServiceDiscoverySort) => void;
  onIssueTypeChange: (issueType: ServiceDiscoveryIssueType) => void;
  onHourlyRateMaxChange: (value: number | null) => void;
  onIncludeUnpricedChange: (value: boolean) => void;
  onResetFilters: () => void;
}

const DISCOVERY_SELECT_BG = 'rgba(14, 18, 16, 0.78)';
const DISCOVERY_SELECT_BG_DISABLED = 'rgba(14, 18, 16, 0.62)';
const DISCOVERY_SELECT_TEXT = 'rgb(247 250 247)';
const DISCOVERY_SELECT_MUTED_TEXT = 'rgb(185 199 189)';
const DISCOVERY_SELECT_DISABLED_TEXT = 'rgb(132 147 138)';
const DISCOVERY_OPTION_BG = 'rgb(22 28 25)';
const DISCOVERY_OPTION_TEXT = 'rgb(247 250 247)';
const DISCOVERY_SORT_OPTIONS: Array<{ value: ServiceDiscoverySort; label: string }> = [
  { value: 'rating', label: 'Ocena' },
  { value: 'reviews', label: 'Liczba opinii' },
  { value: 'name', label: 'Nazwa serwisu' },
];
const DISCOVERY_ISSUE_TYPE_OPTIONS: Array<{ value: ServiceDiscoveryIssueType; label: string }> = [
  { value: null, label: 'Wszystkie' },
  { value: 'N', label: 'Naprawa' },
  { value: 'D', label: 'Diagnostyka' },
  { value: 'U', label: 'Usługa' },
];

const discoverySelectShellBaseClassName =
  'relative min-h-[42px] rounded-lg shadow-none transition-colors duration-150 focus-within:ring-2 focus-within:ring-ratuj-300/70 md:min-h-[44px]';

const discoverySelectClassName =
  'h-[42px] w-full appearance-none rounded-lg border-0 bg-transparent text-sm outline-none ring-0 disabled:cursor-not-allowed md:h-[44px]';

const getDiscoverySelectShellClassName = (disabled: boolean) =>
  `${discoverySelectShellBaseClassName} ${
    disabled ? 'bg-primary-dark-100/60' : 'bg-primary-dark-100/72 hover:bg-primary-dark-200/82'
  }`;

const getDiscoverySelectStyle = (textColor: string, disabled: boolean) =>
  ({
    backgroundColor: disabled ? DISCOVERY_SELECT_BG_DISABLED : DISCOVERY_SELECT_BG,
    color: disabled ? DISCOVERY_SELECT_DISABLED_TEXT : textColor,
    WebkitTextFillColor: disabled ? DISCOVERY_SELECT_DISABLED_TEXT : textColor,
    colorScheme: 'dark',
  }) as const;

const nativeDropdownOptionStyle = {
  color: DISCOVERY_OPTION_TEXT,
  backgroundColor: DISCOVERY_OPTION_BG,
} as const;

const getDiscoverySortLabel = (sortBy: ServiceDiscoverySort) =>
  DISCOVERY_SORT_OPTIONS.find((option) => option.value === sortBy)?.label || 'Ocena';

const DiscoveryIssueTypeField: React.FC<{
  value: ServiceDiscoveryIssueType;
  disabled: boolean;
  onChange: (issueType: ServiceDiscoveryIssueType) => void;
}> = ({ value, disabled, onChange }) => (
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup" aria-label="Typ sprawy">
    {DISCOVERY_ISSUE_TYPE_OPTIONS.map((option) => {
      const isActive = value === option.value;

      return (
        <button
          key={option.value || 'all'}
          type="button"
          role="radio"
          aria-checked={isActive}
          className={`focus-outline inline-flex min-h-[38px] items-center justify-center rounded-lg border px-3 text-xs font-semibold transition-colors duration-150 ${
            isActive
              ? 'border-ratuj-300/55 bg-ratuj-300/14 text-ratuj-100'
              : 'border-primary-dark-300/22 bg-primary-dark-100/10 text-primary-light-300 hover:bg-primary-dark-300/18 hover:text-primary-light-100'
          } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const DiscoveryCityField: React.FC<DiscoveryCityFieldProps> = ({
  value,
  cities,
  disabled,
  onChange,
}) => {
  const hasValue = value.trim().length > 0;

  return (
    <div className="relative min-w-0">
      <MapPinIcon className="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-primary-light-400/80 md:left-3.5" />
      <div className={getDiscoverySelectShellClassName(disabled)}>
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          className={`${discoverySelectClassName} pl-10 pr-20 md:pl-11 md:pr-24`}
          style={getDiscoverySelectStyle(
            hasValue ? DISCOVERY_SELECT_TEXT : DISCOVERY_SELECT_MUTED_TEXT,
            disabled
          )}
          aria-label="Miasto"
          title={value || 'Wszystkie miasta'}
        >
          <option value="" style={nativeDropdownOptionStyle}>
            Wszystkie miasta
          </option>
          {cities.map((cityOption) => (
            <option key={cityOption} value={cityOption} style={nativeDropdownOptionStyle}>
              {cityOption}
            </option>
          ))}
        </select>

        {hasValue && !disabled && (
          <button
            type="button"
            className="focus-outline absolute inset-y-0 right-8 z-[2] inline-flex min-h-[32px] w-8 items-center justify-center rounded-full text-primary-light-400 transition-colors duration-150 hover:bg-primary-dark-300/45 hover:text-primary-light-200 md:right-9"
            onClick={() => onChange('')}
            title="Wyczyść miasto"
            aria-label="Wyczyść miasto"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}

        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-primary-light-400/80">
          <ChevronDownIcon className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
};

const DiscoverySortField: React.FC<DiscoverySortFieldProps> = ({
  sortBy,
  disabled,
  onChange,
  className = '',
}) => (
  <div role="radiogroup" aria-label="Sortowanie serwisów" className={`${className} space-y-2`}>
    {DISCOVERY_SORT_OPTIONS.map((option) => {
      const isActive = sortBy === option.value;

      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={isActive}
          className={`focus-outline flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors duration-150 ${
            isActive
              ? 'border-ratuj-300/45 bg-ratuj-300/10 text-primary-light-100'
              : 'border-primary-dark-300/22 bg-primary-dark-100/10 text-primary-light-300 hover:bg-primary-dark-300/18 hover:text-primary-light-100'
          } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
          onClick={() => onChange(option.value)}
          disabled={disabled}
        >
          <span className="text-sm font-medium">{option.label}</span>
          <span
            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors ${
              isActive
                ? 'border-ratuj-300/80 bg-ratuj-300/10'
                : 'border-primary-dark-300/55 bg-primary-dark-100/80'
            }`}
            aria-hidden="true"
          >
            <span
              className={`h-2 w-2 rounded-full ${isActive ? 'bg-ratuj-300' : 'bg-transparent'}`}
            />
          </span>
        </button>
      );
    })}
  </div>
);

const DiscoveryFilterChip: React.FC<{
  label: string;
  onClear: () => void;
}> = ({ label, onClear }) => (
  <button
    type="button"
    className="focus-outline inline-flex h-8 max-w-[13rem] flex-none items-center gap-1.5 rounded-full border border-primary-dark-300/22 bg-primary-dark-100/12 px-2.5 text-xs text-primary-light-200 transition-colors duration-150 hover:bg-primary-dark-300/24 hover:text-primary-light-100"
    onClick={onClear}
    aria-label={`Usuń filtr ${label}`}
    title={`Usuń filtr ${label}`}
  >
    <span className="truncate whitespace-nowrap">{label}</span>
    <XMarkIcon className="h-3.5 w-3.5 shrink-0 text-primary-light-400" />
  </button>
);

const DiscoveryFiltersPanel: React.FC<DiscoveryFilterPanelProps> = ({
  activeView,
  disabled,
  sortBy,
  issueType,
  hourlyRateMax,
  includeUnpriced,
  onSortChange,
  onIssueTypeChange,
  onHourlyRateMaxChange,
  onIncludeUnpricedChange,
}) => {
  const hasActiveHourlyRate = hourlyRateMax !== null;
  const hasPresetMatch =
    hourlyRateMax !== null &&
    DISCOVERY_HOURLY_RATE_PRESETS.some((presetHourlyRate) => presetHourlyRate === hourlyRateMax);
  const showSortField = activeView === 'list';

  return (
    <div className="space-y-5">
      {showSortField && (
        <section className="space-y-3">
          <p className="text-sm font-semibold text-primary-light-100">Sortowanie</p>
          <DiscoverySortField sortBy={sortBy} disabled={disabled} onChange={onSortChange} />
        </section>
      )}

      <section className={showSortField ? 'border-t border-primary-dark-300/18 pt-5' : ''}>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold text-primary-light-100">Typ sprawy</p>
          </div>
          <DiscoveryIssueTypeField
            value={issueType}
            disabled={disabled}
            onChange={onIssueTypeChange}
          />
        </div>
      </section>

      <section className="border-t border-primary-dark-300/18 pt-5">
        <p className="text-sm font-semibold text-primary-light-100">Stawka roboczogodziny</p>
        <p className="mt-1 text-xs leading-5 text-primary-light-400">
          To orientacyjna stawka. Ostateczna cena zależy od diagnozy.
        </p>

        <div className="mt-3 -mx-1 overflow-x-auto pb-1">
          <div className="flex min-w-max items-center gap-2 px-1">
            {DISCOVERY_HOURLY_RATE_PRESETS.map((presetHourlyRate) => {
              const isActive = hourlyRateMax === presetHourlyRate;

              return (
                <button
                  key={presetHourlyRate}
                  type="button"
                  className={`focus-outline inline-flex h-9 flex-none items-center justify-center rounded-full border px-3.5 text-xs font-semibold whitespace-nowrap transition-colors duration-150 ${
                    isActive
                      ? 'border-ratuj-300/55 bg-ratuj-300/14 text-ratuj-100'
                      : 'border-primary-dark-300/24 bg-primary-dark-100/10 text-primary-light-300 hover:bg-primary-dark-300/18 hover:text-primary-light-100'
                  }`}
                  onClick={() => onHourlyRateMaxChange(isActive ? null : presetHourlyRate)}
                  aria-pressed={isActive}
                  disabled={disabled}
                >
                  {formatHourlyRateFilterLabel(presetHourlyRate)}
                </button>
              );
            })}
          </div>
        </div>

        {hasActiveHourlyRate && !hasPresetMatch && (
          <p className="mt-2 text-xs leading-5 text-primary-light-400">
            Aktywny limit z URL: {formatHourlyRateFilterLabel(hourlyRateMax)}
          </p>
        )}
      </section>

      <section className="border-t border-primary-dark-300/18 pt-5">
        <div
          className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 ${
            hasActiveHourlyRate
              ? 'border-primary-dark-300/22 bg-primary-dark-100/10'
              : 'border-primary-dark-300/12 bg-primary-dark-100/6 opacity-70'
          }`}
        >
          <span className="min-w-0 text-sm leading-5 text-primary-light-100">
            Pokazuj też serwisy bez stawki
          </span>
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-primary-dark-300/55 bg-primary-dark-100/80 text-ratuj-300 accent-ratuj-300 focus:ring-ratuj-300/70"
            checked={includeUnpriced}
            onChange={(event) => onIncludeUnpricedChange(event.target.checked)}
            disabled={disabled || !hasActiveHourlyRate}
            aria-label="Pokazuj też serwisy bez stawki"
          />
        </div>
      </section>
    </div>
  );
};

const DiscoveryFilterSheet: React.FC<DiscoveryFilterSheetProps> = ({
  open,
  activeView,
  disabled,
  sortBy,
  issueType,
  hourlyRateMax,
  includeUnpriced,
  resultsCount,
  resultsCountLoading,
  showClearAllAction,
  onSortChange,
  onIssueTypeChange,
  onHourlyRateMaxChange,
  onIncludeUnpricedChange,
  onClose,
  onResetFilters,
  returnFocusRef,
}) => {
  const initialFocusRef = useRef<HTMLButtonElement | null>(null);

  if (!open) {
    return null;
  }

  const footerLabel =
    typeof resultsCount === 'number' && !resultsCountLoading
      ? `Pokaż ${formatServiceDiscoveryCountLabel(resultsCount)}`
      : 'Pokaż wyniki';

  return createPortal(
    <Modal
      isOpen
      onClose={onClose}
      ariaLabelledBy="service-discovery-filters-title"
      initialFocusRef={initialFocusRef}
      returnFocusRef={returnFocusRef}
      placement="bottom-responsive"
      overlayClassName="!z-[80] !bg-black/58 backdrop-blur-sm"
      overlayTestId="service-discovery-filters-overlay"
      dialogTestId="service-discovery-filters-dialog"
      className="relative z-[1] flex !max-h-[88dvh] !w-full flex-col !overflow-hidden !rounded-b-none !rounded-t-2xl !border-primary-dark-300/38 sm:!max-h-[80vh] sm:!max-w-xl sm:!rounded-2xl"
    >
      <div className="flex items-start justify-between gap-3 border-b border-primary-dark-300/18 px-4 py-3.5 sm:px-5">
        <div className="min-w-0">
          <h2
            id="service-discovery-filters-title"
            className="text-base font-semibold text-primary-light-100"
          >
            Filtry
          </h2>
          <p className="mt-1 text-xs leading-5 text-primary-light-400">
            Dopasuj wyniki do sposobu szukania.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {showClearAllAction && (
            <button
              type="button"
              className="focus-outline min-h-[32px] rounded-md px-2 text-xs font-semibold text-primary-light-300 transition-colors hover:bg-primary-dark-300/24 hover:text-primary-light-100"
              onClick={onResetFilters}
              disabled={disabled}
            >
              Wyczyść wszystko
            </button>
          )}

          <button
            ref={initialFocusRef}
            type="button"
            className="focus-outline inline-flex h-8 w-8 items-center justify-center rounded-full text-primary-light-400 transition-colors hover:bg-primary-dark-300/24 hover:text-primary-light-100"
            onClick={onClose}
            aria-label="Zamknij filtry"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
        <DiscoveryFiltersPanel
          activeView={activeView}
          disabled={disabled}
          sortBy={sortBy}
          issueType={issueType}
          hourlyRateMax={hourlyRateMax}
          includeUnpriced={includeUnpriced}
          onSortChange={onSortChange}
          onIssueTypeChange={onIssueTypeChange}
          onHourlyRateMaxChange={onHourlyRateMaxChange}
          onIncludeUnpricedChange={onIncludeUnpricedChange}
        />
      </div>

      <div className="border-t border-primary-dark-300/18 bg-primary-dark-200/96 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:p-4">
        <button
          type="button"
          className="focus-outline min-h-[44px] w-full rounded-lg bg-ratuj-400 px-4 text-sm font-semibold text-white shadow-button transition-colors hover:bg-ratuj-300"
          onClick={onClose}
        >
          {footerLabel}
        </button>
      </div>
    </Modal>,
    document.body
  );
};

const ServiceDiscoveryControls: React.FC<ServiceDiscoveryControlsProps> = ({
  activeView,
  mapUrl,
  listUrl,
  resultsCount,
  resultsCountLoading = false,
  showResultsCount = true,
  query,
  city,
  sortBy,
  issueType,
  hourlyRateMax,
  includeUnpriced,
  cities,
  services,
  smartSelection,
  disabled = false,
  onSearchChange,
  onCityChange,
  onSortChange,
  onIssueTypeChange,
  onHourlyRateMaxChange,
  onIncludeUnpricedChange,
  onResetFilters,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersTriggerRef = useRef<HTMLButtonElement | null>(null);
  const hasResultsCount = showResultsCount && typeof resultsCount === 'number';
  const safeResultsCount = resultsCount ?? 0;
  const trimmedQuery = query.trim();
  const trimmedCity = city.trim();
  const hasActiveQuery = trimmedQuery.length > 0;
  const hasActiveCity = trimmedCity.length > 0;
  const hasActiveSort = activeView === 'list' && sortBy !== DEFAULT_DISCOVERY_SORT;
  const hasActiveIssueType = issueType !== null;
  const hasActiveHourlyRate = hourlyRateMax !== null;
  const hasExplicitUnpricedExclusion = hasActiveHourlyRate && !includeUnpriced;
  const showClearAllAction =
    hasActiveQuery ||
    hasActiveCity ||
    hasActiveSort ||
    hasActiveIssueType ||
    hasActiveHourlyRate ||
    hasExplicitUnpricedExclusion;
  const advancedFilterCount =
    Number(hasActiveSort) +
    Number(hasActiveIssueType) +
    Number(hasActiveHourlyRate) +
    Number(hasExplicitUnpricedExclusion);
  const showActiveSummaryRail =
    hasActiveSort || hasActiveHourlyRate || hasExplicitUnpricedExclusion;
  const resultsCountLabel = resultsCountLoading
    ? 'Ładowanie serwisów...'
    : formatServiceDiscoveryCountLabel(safeResultsCount);
  const sortLabel = getDiscoverySortLabel(sortBy);
  const issueTypeLabel = formatDiscoveryIssueTypeLabel(issueType);
  const filterButtonClassName = `rs-discovery-filter-trigger focus-outline inline-flex h-[42px] shrink-0 items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors duration-150 md:h-[44px] ${
    filtersOpen || advancedFilterCount > 0
      ? 'bg-ratuj-300/10 text-ratuj-100'
      : 'text-primary-light-300 hover:text-primary-light-100'
  }`;
  const mapViewLinkClass = (view: 'map' | 'list') =>
    `focus-outline inline-flex h-[34px] flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-semibold transition-colors duration-150 md:h-[36px] ${
      activeView === view
        ? 'bg-ratuj-300/18 text-ratuj-100 ring-1 ring-inset ring-ratuj-300/42'
        : 'text-primary-light-300 hover:bg-primary-dark-300/24 hover:text-primary-light-100'
    }`;

  const renderActiveSummaryRail = () =>
    showActiveSummaryRail ? (
      <div
        data-testid="service-discovery-chip-rail"
        className="mt-2 overflow-x-auto overflow-y-hidden pb-1"
      >
        <div className="flex min-w-max items-center gap-2 px-0.5 whitespace-nowrap">
          {hasActiveSort && (
            <DiscoveryFilterChip
              label={`Sort: ${sortLabel}`}
              onClear={() => onSortChange(DEFAULT_DISCOVERY_SORT)}
            />
          )}

          {hasActiveHourlyRate && (
            <DiscoveryFilterChip
              label={formatHourlyRateFilterLabel(hourlyRateMax)}
              onClear={() => onHourlyRateMaxChange(null)}
            />
          )}

          {hasExplicitUnpricedExclusion && (
            <DiscoveryFilterChip
              label="Tylko z podaną stawką"
              onClear={() => onIncludeUnpricedChange(true)}
            />
          )}
        </div>
      </div>
    ) : null;

  const renderTopControls = () => (
    <div className="rs-discovery-console-grid">
      <div data-testid="service-discovery-search-row" className="rs-discovery-search-row min-w-0">
        <SearchBar
          onSearch={onSearchChange}
          value={query}
          selectedSuggestion={smartSelection}
          services={services}
          disabled={disabled}
          placeholder="Np. pralka nie wiruje, diagnostyka auta, instalacja Windowsa"
        />
      </div>

      <div
        data-testid="service-discovery-context-row"
        className="rs-discovery-context-row flex min-w-0 flex-wrap items-center gap-2"
      >
        {hasResultsCount && (
          <div
            data-testid="service-discovery-results-pill"
            className="rs-discovery-results-pill inline-flex h-[42px] shrink-0 items-center rounded-lg px-3 text-xs font-semibold text-primary-light-300 md:h-[44px]"
          >
            <span className="whitespace-nowrap">
              {resultsCountLoading ? 'Ładowanie...' : resultsCountLabel}
            </span>
          </div>
        )}

        <div className="min-w-[min(13rem,100%)] flex-1">
          <DiscoveryCityField
            value={city}
            cities={cities}
            disabled={disabled}
            onChange={onCityChange}
          />
        </div>
      </div>

      <div
        data-testid="service-discovery-view-row"
        className="rs-discovery-view-row flex min-w-0 items-center gap-2"
      >
        <div className="rs-discovery-view-switch flex min-w-0 flex-1 items-center rounded-lg p-1">
          <Link to={mapUrl} className={mapViewLinkClass('map')} title="Mapa">
            <MapIcon className="h-4 w-4" />
            <span>Mapa</span>
          </Link>
          <Link to={listUrl} className={mapViewLinkClass('list')} title="Lista">
            <ViewColumnsIcon className="h-4 w-4" />
            <span>Lista</span>
          </Link>
        </div>

        <button
          ref={filtersTriggerRef}
          type="button"
          className={filterButtonClassName}
          onClick={() => setFiltersOpen(true)}
          aria-expanded={filtersOpen}
          aria-haspopup="dialog"
          title={hasActiveIssueType ? `Typ sprawy: ${issueTypeLabel}` : 'Filtry'}
        >
          <AdjustmentsHorizontalIcon className="h-4 w-4" />
          <span>Filtry</span>
          {advancedFilterCount > 0 && (
            <span className="inline-flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-ratuj-300/18 px-1 text-[10px] font-bold text-ratuj-100">
              {advancedFilterCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );

  const renderFilterSheet = () => (
    <DiscoveryFilterSheet
      open={filtersOpen}
      activeView={activeView}
      disabled={disabled}
      sortBy={sortBy}
      issueType={issueType}
      hourlyRateMax={hourlyRateMax}
      includeUnpriced={includeUnpriced}
      resultsCount={resultsCount}
      resultsCountLoading={resultsCountLoading}
      showClearAllAction={showClearAllAction}
      onSortChange={onSortChange}
      onIssueTypeChange={onIssueTypeChange}
      onHourlyRateMaxChange={onHourlyRateMaxChange}
      onIncludeUnpricedChange={onIncludeUnpricedChange}
      onResetFilters={onResetFilters}
      onClose={() => setFiltersOpen(false)}
      returnFocusRef={filtersTriggerRef}
    />
  );

  return (
    <>
      <section
        className="rs-discovery-console relative z-[1000] overflow-visible"
        aria-label={
          activeView === 'map'
            ? 'Wyszukiwanie serwisów na mapie'
            : 'Wyszukiwanie serwisów na liście'
        }
      >
        {renderTopControls()}
        {renderActiveSummaryRail()}
      </section>

      {renderFilterSheet()}
    </>
  );
};

export default ServiceDiscoveryControls;
